import {forwardRef} from 'react';
import {cn} from '../lib/cn';
import {type CheckboxOption} from '../checkbox';
import {DropdownMenuCheckboxItem} from './DropdownMenu';
import {type CheckboxDropdownGroupProps} from './types';

const CheckboxDropdownGroup = forwardRef<HTMLDivElement, CheckboxDropdownGroupProps>(
  ({options, value = [], onChange, className = '', keepOpenOnSelect, multiple}, ref) => {
    const isMultipleMode = multiple ?? Array.isArray(value);

    const normalizedValue = Array.isArray(value) ? value : value ? [value] : [];

    const handleCheckboxChange = (optionValue: CheckboxOption, checked: boolean) => {
      if (isMultipleMode) {
        const newValue = checked
          ? [...normalizedValue, optionValue]
          : normalizedValue.filter(v => v.value !== optionValue.value);
        (onChange as (selectedValues: CheckboxOption[]) => void)(newValue);
      } else {
        if (checked) {
          (onChange as (selectedValues: CheckboxOption) => void)(optionValue);
        } else {
          (onChange as (selectedValues: CheckboxOption | null) => void)(null);
        }
      }
    };

    const isChecked = (optionValue: string) =>
      normalizedValue.some(v => v.value === optionValue);

    const renderCheckboxItem = (option: CheckboxOption) => {
      const checked = option.checked ?? isChecked(option.value);

      return (
        <DropdownMenuCheckboxItem
          key={option.value}
          checked={checked}
          onSelect={e => {
            if (keepOpenOnSelect) {
              e.preventDefault();
            }
          }}
          onCheckedChange={newChecked => {
            if (!isMultipleMode && newChecked) {
              handleCheckboxChange(option, newChecked);
            } else {
              handleCheckboxChange(option, newChecked);
            }
          }}
          disabled={option.disabled}
          className={cn('cursor-pointer', option.disabled && 'cursor-not-allowed')}
        >
          {option.label}
        </DropdownMenuCheckboxItem>
      );
    };

    return (
      <div ref={ref} className={className}>
        {options.map(renderCheckboxItem)}
      </div>
    );
  },
);

CheckboxDropdownGroup.displayName = 'CheckboxDropdownGroup';

export {CheckboxDropdownGroup};
